import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  FormControl,
  FormLabel,
  Select,
  useColorModeValue,
  Badge,
  Divider,
  SimpleGrid,
  Icon,
  Checkbox,
} from '@chakra-ui/react';
import { Link } from '@chakra-ui/react';
import { CheckIcon, TimeIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { useContext } from 'react';
import { AppContext } from './AppContext';

const WorkflowManagement = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  
  // Step 2 form state
  const [selectedPayer, setSelectedPayer] = useState('BCBS');
  const [selectedState, setSelectedState] = useState('Texas');

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const successBg = useColorModeValue('green.50', 'green.900');
  const pendingBg = useColorModeValue('blue.50', 'blue.900');
  const instructionsBg = useColorModeValue('blue.50', 'blue.900');

  // Payer options
  const payerOptions = ['BCBS', 'UHC', 'Aetna', 'CIGNA', 'Medicare'];
  
  // State options (simplified list for demo)
  const stateOptions = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  // Load workflow status on component mount and after successful operations
  const loadWorkflowStatus = async () => {
    try {
      const response = await callApi(`/workflow_status/${caseId}`, accessToken);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setWorkflowStatus(data);
      setError(null);
    } catch (err) {
      console.error('Error loading workflow status:', err);
      setError(err.message);
      setWorkflowStatus(null);
    }
  };

  // Load status on mount
  useEffect(() => {
    if (caseId && accessToken) {
      loadWorkflowStatus();
    }
  }, [caseId, accessToken]);

  // Execute Step 1: Baseline Coding
  const executeStep1 = async () => {
    setLoading(true);
    setCurrentStep(1);
    setError(null);

    try {
      const response = await callApi('/cpt_coding', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          case_type: 'Clinical',
          background: false
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // Refresh workflow status after successful completion
      await loadWorkflowStatus();
    } catch (err) {
      console.error('Error executing Step 1:', err);
      setError(`Step 1 failed: ${err.message}`);
    } finally {
      setLoading(false);
      setCurrentStep(null);
    }
  };

  // Execute Step 2: Carrier Assignment
  const executeStep2 = async () => {
    setLoading(true);
    setCurrentStep(2);
    setError(null);

    try {
      const response = await callApi('/assign_carrier', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          payer_type: selectedPayer,
          carrier_name: selectedPayer,
          state_full: selectedState
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // Refresh workflow status after successful completion
      await loadWorkflowStatus();
    } catch (err) {
      console.error('Error executing Step 2:', err);
      setError(`Step 2 failed: ${err.message}`);
    } finally {
      setLoading(false);
      setCurrentStep(null);
    }
  };

  const [runNcciValidation, setRunNcciValidation] = useState(false);

  // Execute Step 3: Carrier Adjustments
  const executeStep3 = async () => {
    setLoading(true);
    setCurrentStep(3);
    setError(null);

    try {
      let endpoint = '/apply_carrier_rules';
      let payload = {
        case_id: caseId,
        force_refresh: true,
      };

      if (runNcciValidation) {
        endpoint = '/apply_carrier_rules_with_ncci';
        // The new endpoint handles both steps, no need to pass the flag
      }

      const response = await callApi(endpoint, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // Refresh workflow status after successful completion
      await loadWorkflowStatus();
    } catch (err) {
      console.error('Error executing Step 3:', err);
      setError(`Step 3 failed: ${err.message}`);
    } finally {
      setLoading(false);
      setCurrentStep(null);
    }
  };

  // Component for displaying individual step status
  const StepDisplay = ({ stepNumber, stepName, stepData, isReady, onExecute, children }) => {
    const isComplete = stepData?.completed || false;
    const isCurrentlyRunning = loading && currentStep === stepNumber;
    
    return (
      <Card 
        bg={isComplete ? successBg : (isReady ? pendingBg : cardBg)} 
        borderColor={borderColor}
        borderWidth="2px"
      >
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center">
              <HStack spacing={3}>
                <Icon 
                  as={isComplete ? CheckIcon : TimeIcon} 
                  color={isComplete ? 'green.500' : 'blue.500'}
                  boxSize={5}
                />
                <VStack align="start" spacing={0}>
                  <Text fontSize="lg" fontWeight="semibold">
                    Step {stepNumber}: {stepName}
                  </Text>
                  {stepData?.details && (
                    <Text fontSize="sm" color="gray.600">
                      {stepData.details}
                    </Text>
                  )}
                </VStack>
              </HStack>
              
              <Badge 
                colorScheme={isComplete ? 'green' : (isReady ? 'blue' : 'gray')}
                fontSize="sm"
                px={3}
                py={1}
              >
                {isComplete ? 'Complete' : (isReady ? 'Ready' : 'Waiting')}
              </Badge>
            </HStack>

            {/* Step-specific content */}
            {children}

            {/* Action button */}
            {isReady && !isComplete && (
              <Button
                colorScheme="blue"
                size="lg"
                onClick={onExecute}
                isLoading={isCurrentlyRunning}
                loadingText={`Executing Step ${stepNumber}...`}
                isDisabled={loading}
              >
                {stepNumber === 1 && 'Run Baseline Coding'}
                {stepNumber === 2 && 'Assign Carrier'}
                {stepNumber === 3 && 'Apply Carrier Rules'}
              </Button>
            )}

            {/* Re-run button for Step 3 */}
            {isComplete && stepNumber === 3 && (
              <Button
                variant="outline"
                colorScheme="blue"
                size="md"
                onClick={onExecute}
                isLoading={isCurrentlyRunning}
                loadingText={`Re-running Step ${stepNumber}...`}
                isDisabled={loading}
              >
                Re-run Carrier Adjustments
              </Button>
            )}

            {/* Show additional details if complete */}
            {isComplete && stepData && (
              <VStack align="stretch" spacing={2}>
                {stepData.timestamp && (
                  <Text fontSize="sm" color="gray.600">
                    Completed: {new Date(stepData.timestamp).toLocaleString()}
                  </Text>
                )}
                {stepData.code_count && (
                  <Text fontSize="sm" color="gray.600">
                    Generated {stepData.code_count} CPT codes
                  </Text>
                )}
                {stepData.carrier_name && (
                  <Text fontSize="sm" color="gray.600">
                    Carrier: {stepData.carrier_name}
                  </Text>
                )}
                {stepData.adjustment_count && (
                  <Text fontSize="sm" color="gray.600">
                    Applied {stepData.adjustment_count} adjustment(s)
                  </Text>
                )}
              </VStack>
            )}
          </VStack>
        </CardBody>
      </Card>
    );
  };

  // Determine which steps are ready based on workflow status
  const getStepReadiness = () => {
    if (!workflowStatus) {
      return { step1Ready: true, step2Ready: false, step3Ready: false };
    }

    const step1Complete = workflowStatus.step1_baseline_coding?.completed || false;
    const step2Complete = workflowStatus.step2_carrier_assignment?.completed || false;
    const step3Complete = workflowStatus.step3_carrier_adjustments?.completed || false;

    return {
      step1Ready: !step1Complete,
      step2Ready: step1Complete && !step2Complete,
      step3Ready: step1Complete && step2Complete && !step3Complete
    };
  };

  const { step1Ready, step2Ready, step3Ready } = getStepReadiness();

  const allStepsComplete = workflowStatus?.step1_baseline_coding?.completed && workflowStatus?.step2_carrier_assignment?.completed && workflowStatus?.step3_carrier_adjustments?.completed;

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box>
            <Heading as="h1" size="xl" mb={2}>
              Workflow Management
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Execute the 3-step modern coding workflow for case processing
            </Text>
          </Box>

          {/* Case Info Card */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Case Information</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">CASE ID</Text>
                  <Text fontSize="lg">{caseId}</Text>
                </Box>
                {workflowStatus?.next_actions && (
                  <Box>
                    <Text fontSize="sm" color="gray.600" fontWeight="semibold">NEXT ACTIONS</Text>
                    <Text fontSize="lg">{workflowStatus.next_actions.join(', ')}</Text>
                  </Box>
                )}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Error Display */}
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Workflow Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Workflow Steps */}
          {workflowStatus && (
            <VStack spacing={4} align="stretch">
              {/* Step 1: Baseline Coding */}
              <StepDisplay
                stepNumber={1}
                stepName="Baseline Coding"
                stepData={workflowStatus.step1_baseline_coding}
                isReady={step1Ready}
                onExecute={executeStep1}
              />

              {/* Step 2: Carrier Assignment */}
              <StepDisplay
                stepNumber={2}
                stepName="Carrier Assignment"
                stepData={workflowStatus.step2_carrier_assignment}
                isReady={step2Ready}
                onExecute={executeStep2}
              >
                {/* Step 2 Form - only show when step is ready */}
                {step2Ready && (
                  <VStack spacing={4} align="stretch">
                    <Divider />
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel>Payer</FormLabel>
                        <Select 
                          value={selectedPayer} 
                          onChange={(e) => setSelectedPayer(e.target.value)}
                        >
                          {payerOptions.map(payer => (
                            <option key={payer} value={payer}>{payer}</option>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel>State</FormLabel>
                        <Select 
                          value={selectedState} 
                          onChange={(e) => setSelectedState(e.target.value)}
                        >
                          {stateOptions.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </Select>
                      </FormControl>
                    </SimpleGrid>
                  </VStack>
                )}
              </StepDisplay>

              {/* Step 3: Carrier Adjustments */}
              <StepDisplay
                stepNumber={3}
                stepName="Carrier Adjustments"
                stepData={workflowStatus.step3_carrier_adjustments}
                isReady={step3Ready}
                onExecute={executeStep3}
              >
                {/* NCCI Validation Checkbox - only show when step is ready or complete */}
                {(step3Ready || workflowStatus.step3_carrier_adjustments?.completed) && (
                  <VStack spacing={4} align="stretch" pt={4}>
                    <Divider />
                    <FormControl>
                      <Checkbox
                        isChecked={runNcciValidation}
                        onChange={(e) => setRunNcciValidation(e.target.checked)}
                      >
                        Include NCCI Validation
                      </Checkbox>
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        If checked, the system will perform NCCI PTP validation after applying carrier rules.
                      </Text>
                    </FormControl>
                  </VStack>
                )}
              </StepDisplay>
            </VStack>
          )}

          {/* Next Steps - Medical Necessity Analysis */}
          {workflowStatus && allStepsComplete && (
            <Card bg={cardBg} borderColor="green.200" borderWidth="2px">
              <CardHeader>
                <Heading size="md" color="green.600">🎉 Workflow Complete!</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <Text>All workflow steps have been completed successfully. You can now proceed to medical necessity analysis.</Text>
                  <Link as={RouterLink} to={`/analyze-case-mn/${caseId}`}>
                    <Button colorScheme="green" size="lg" width="100%">
                      Proceed to Medical Necessity Analysis
                    </Button>
                  </Link>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Loading State for Initial Load */}
          {!workflowStatus && !error && (
            <Card bg={cardBg} borderColor={borderColor}>
              <CardBody>
                <VStack spacing={4}>
                  <Spinner size="xl" color="blue.500" />
                  <Text fontSize="lg">Loading workflow status...</Text>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Instructions */}
          {workflowStatus && !loading && (
            <Card bg={instructionsBg} borderColor="blue.200">
              <CardBody>
                <VStack spacing={3} align="start">
                  <Heading size="sm">Workflow Steps:</Heading>
                  <Text fontSize="sm">
                    <strong>Step 1:</strong> Generate baseline CPT codes from case content
                  </Text>
                  <Text fontSize="sm">
                    <strong>Step 2:</strong> Assign carrier/payer information and state
                  </Text>
                  <Text fontSize="sm">
                    <strong>Step 3:</strong> Apply carrier-specific coding adjustments
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Note:</strong> Steps must be completed in order. Each step will become available after the previous step completes successfully.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default WorkflowManagement;
